import React, { useEffect, useState, useCallback } from "react";
import { Card, CardBody, Row, Col, Table, Button, Badge, Spinner, Nav, NavItem, NavLink, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Input } from "reactstrap";
import classnames from "classnames";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import CreateLeadModal from "../../Modal/Leads/CreateLeadModal";
import { getMyAddedLeadsApi, getPendingApprovalsApi, approveLeadApi, rejectLeadApi } from "../../apiServices/genericLeadServices";

const MyAddedLeads = () => {
  const navigate = useNavigate();
  const authUser = JSON.parse(localStorage.getItem("authUser"))?.response;
  const isSuperAdmin = authUser?.role === "super_admin";

  const [activeTab, setActiveTab] = useState("myAdded");
  const [createModal, setCreateModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // My Added Leads state
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, approved: 0, pending: 0, rejected: 0 });
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [statusFilter, setStatusFilter] = useState("all");

  // Pending Approvals state
  const [pendingLeads, setPendingLeads] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingPagination, setPendingPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });

  // Rejection Modal state
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedLeadToReject, setSelectedLeadToReject] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectSubmitting, setRejectSubmitting] = useState(false);

  // Rejection details view modal
  const [viewReasonModal, setViewReasonModal] = useState({ open: false, reason: "", leadName: "" });

  // Fetch My Added Leads
  const fetchMyAddedLeads = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getMyAddedLeadsApi({
        page: pagination.page,
        limit: pagination.limit,
        status: statusFilter,
      });
      if (res?.success) {
        setLeads(res?.data?.leads || []);
        if (res?.data?.stats) setStats(res.data.stats);
        if (res?.data?.pagination) setPagination(res.data.pagination);
      }
    } catch (err) {
      toast.error(err?.message || "Failed to load added leads.");
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, statusFilter]);

  // Fetch Pending Approvals (for superadmin or reporting managers)
  const fetchPendingApprovals = useCallback(async () => {
    try {
      setPendingLoading(true);
      const res = await getPendingApprovalsApi({
        page: pendingPagination.page,
        limit: pendingPagination.limit,
      });
      if (res?.success) {
        setPendingLeads(res?.data?.leads || []);
        if (res?.data?.pagination) setPendingPagination(res.data.pagination);
      }
    } catch (err) {
      console.error("Failed to load pending approvals", err);
    } finally {
      setPendingLoading(false);
    }
  }, [pendingPagination.page, pendingPagination.limit]);

  useEffect(() => {
    fetchMyAddedLeads();
  }, [fetchMyAddedLeads, refreshKey]);

  useEffect(() => {
    fetchPendingApprovals();
  }, [fetchPendingApprovals, refreshKey]);

  const handleApprove = async (leadId) => {
    try {
      const res = await approveLeadApi(leadId);
      toast.success(res?.message || "Lead approved successfully!");
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      toast.error(err?.message || "Failed to approve lead.");
    }
  };

  const handleOpenReject = (lead) => {
    setSelectedLeadToReject(lead);
    setRejectionReason("");
    setRejectModalOpen(true);
  };

  const handleSubmitReject = async (e) => {
    e.preventDefault();
    if (!rejectionReason.trim()) {
      return toast.warning("Please provide a reason for rejecting the lead.");
    }

    try {
      setRejectSubmitting(true);
      const res = await rejectLeadApi(selectedLeadToReject._id, {
        rejectionReason: rejectionReason.trim(),
      });
      toast.success(res?.message || "Lead rejected successfully.");
      setRejectModalOpen(false);
      setSelectedLeadToReject(null);
      setRejectionReason("");
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      toast.error(err?.message || "Failed to reject lead.");
    } finally {
      setRejectSubmitting(false);
    }
  };

  const formatDateTime = (dateVal) => {
    if (!dateVal) return "-";
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderApprovalStatusBadge = (lead) => {
    const status = lead?.approvalStatus || "approved";
    if (status === "approved") {
      return (
        <Badge color="success" pill className="px-2 py-1">
          <i className="mdi mdi-check-circle me-1" /> Approved
        </Badge>
      );
    }
    if (status === "rejected") {
      return (
        <span className="d-inline-flex align-items-center gap-1">
          <Badge color="danger" pill className="px-2 py-1">
            <i className="mdi mdi-close-circle me-1" /> Rejected
          </Badge>
          {lead?.rejectionReason && (
            <Button
              color="link"
              size="sm"
              className="p-0 text-danger"
              title="View rejection reason"
              onClick={() =>
                setViewReasonModal({
                  open: true,
                  reason: lead.rejectionReason,
                  leadName: lead.name || "Lead",
                })
              }
            >
              <i className="mdi mdi-information-outline fs-6" />
            </Button>
          )}
        </span>
      );
    }
    return (
      <Badge color="warning" pill className="px-2 py-1 text-dark">
        <i className="mdi mdi-clock-outline me-1" /> Pending Approval
      </Badge>
    );
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Leads" breadcrumbItem="My Added Leads" />

          {/* 🌟 SMALL DASHBOARD STATS WIDGETS */}
          <Row className="mb-4">
            <Col xl={3} md={6}>
              <Card className="mini-stat bg-white text-dark shadow-sm border-0 h-100">
                <CardBody className="p-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <p className="text-muted fw-medium mb-1 small text-uppercase">Total Added Leads</p>
                      <h4 className="fw-bold mb-0 text-primary">{stats.total}</h4>
                    </div>
                    <div className="avatar-sm rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center">
                      <i className="mdi mdi-account-multiple text-primary fs-4" />
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col xl={3} md={6}>
              <Card className="mini-stat bg-white text-dark shadow-sm border-0 h-100">
                <CardBody className="p-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <p className="text-muted fw-medium mb-1 small text-uppercase">Approved Leads</p>
                      <h4 className="fw-bold mb-0 text-success">{stats.approved}</h4>
                    </div>
                    <div className="avatar-sm rounded-circle bg-success bg-opacity-10 d-flex align-items-center justify-content-center">
                      <i className="mdi mdi-check-decagram text-success fs-4" />
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col xl={3} md={6}>
              <Card className="mini-stat bg-white text-dark shadow-sm border-0 h-100">
                <CardBody className="p-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <p className="text-muted fw-medium mb-1 small text-uppercase">Pending Approval</p>
                      <h4 className="fw-bold mb-0 text-warning">{stats.pending}</h4>
                    </div>
                    <div className="avatar-sm rounded-circle bg-warning bg-opacity-10 d-flex align-items-center justify-content-center">
                      <i className="mdi mdi-clock-outline text-warning fs-4" />
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col xl={3} md={6}>
              <Card className="mini-stat bg-white text-dark shadow-sm border-0 h-100">
                <CardBody className="p-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <p className="text-muted fw-medium mb-1 small text-uppercase">Rejected Leads</p>
                      <h4 className="fw-bold mb-0 text-danger">{stats.rejected}</h4>
                    </div>
                    <div className="avatar-sm rounded-circle bg-danger bg-opacity-10 d-flex align-items-center justify-content-center">
                      <i className="mdi mdi-close-circle-outline text-danger fs-4" />
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* MAIN CARD CONTAINER */}
          <Card className="shadow-sm border-0">
            <CardBody className="p-3">
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                {/* TABS */}
                <Nav tabs className="nav-tabs-custom border-bottom-0">
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === "myAdded" })}
                      onClick={() => setActiveTab("myAdded")}
                      style={{ cursor: "pointer" }}
                    >
                      <i className="mdi mdi-account-arrow-right me-1" />
                      My Added Leads ({stats.total})
                    </NavLink>
                  </NavItem>

                  {(isSuperAdmin || pendingLeads.length > 0) && (
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === "pendingApprovals" })}
                        onClick={() => setActiveTab("pendingApprovals")}
                        style={{ cursor: "pointer" }}
                      >
                        <i className="mdi mdi-gavel me-1 text-warning" />
                        Pending Approvals
                        {pendingPagination.total > 0 && (
                          <Badge color="danger" pill className="ms-2">
                            {pendingPagination.total}
                          </Badge>
                        )}
                      </NavLink>
                    </NavItem>
                  )}
                </Nav>

                {/* ACTION BUTTONS */}
                <div className="d-flex align-items-center gap-2">
                  {activeTab === "myAdded" && (
                    <Input
                      type="select"
                      size="sm"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      style={{ width: "160px" }}
                    >
                      <option value="all">All Statuses</option>
                      <option value="pending">Pending Approval</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                    </Input>
                  )}

                  <Button
                    color="primary"
                    className="fw-semibold d-flex align-items-center gap-1"
                    onClick={() => setCreateModal(true)}
                  >
                    <i className="mdi mdi-plus" /> Add Lead
                  </Button>

                  <Button
                    color="light"
                    onClick={() => setRefreshKey((prev) => prev + 1)}
                    title="Refresh"
                  >
                    <i className="mdi mdi-refresh" />
                  </Button>
                </div>
              </div>

              {/* TAB 1: MY ADDED LEADS */}
              {activeTab === "myAdded" && (
                <div>
                  {loading ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                      <p className="mt-2 text-muted">Loading your added leads...</p>
                    </div>
                  ) : leads.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <i className="mdi mdi-account-off-outline fs-1 text-secondary d-block mb-2" />
                      <h5>No leads added yet</h5>
                      <p className="small mb-3">Click on "Add Lead" above to create and submit a new lead.</p>
                      <Button color="primary" size="sm" onClick={() => setCreateModal(true)}>
                        <i className="mdi mdi-plus me-1" /> Add Your First Lead
                      </Button>
                    </div>
                  ) : isSuperAdmin ? (
                    /* 👑 SUPER ADMIN DETAILED TABLE VIEW */
                    <div className="table-responsive">
                      <Table hover className="align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Lead Name</th>
                            <th>Contact</th>
                            <th>Project</th>
                            <th>Stage</th>
                            <th>Assigned To</th>
                            <th>Source</th>
                            <th>Approval Status</th>
                            <th>Created / Updated</th>
                            <th className="text-center">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {leads.map((lead) => (
                            <tr key={lead._id}>
                              <td>
                                <div className="fw-semibold text-primary">{lead.name || "-"}</div>
                              </td>
                              <td>
                                <div className="small text-muted">{lead.phone || "-"}</div>
                                <div className="small text-muted">{lead.email || "-"}</div>
                              </td>
                              <td>
                                <span className="small fw-semibold">{lead.projectName || "-"}</span>
                              </td>
                              <td>
                                <Badge color="info" pill className="px-2 py-1">
                                  {lead.stageName || "-"}
                                </Badge>
                              </td>
                              <td>
                                {lead.assignedToName && lead.assignedToName !== "-" ? (
                                  <span className="small fw-semibold">{lead.assignedToName}</span>
                                ) : (
                                  <Badge color="light" className="text-secondary border">
                                    Unassigned
                                  </Badge>
                                )}
                              </td>
                              <td>
                                <Badge color="secondary" pill className="text-capitalize">
                                  {lead.source || "generic"}
                                </Badge>
                              </td>
                              <td>{renderApprovalStatusBadge(lead)}</td>
                              <td>
                                <div className="small text-muted">{formatDateTime(lead.createdAt || lead.createdTime)}</div>
                                <div className="small text-muted fst-italic">Upd: {formatDateTime(lead.updatedAt)}</div>
                              </td>
                              <td className="text-center">
                                <Button
                                  color="light"
                                  size="sm"
                                  title="View Lead Details"
                                  onClick={() => navigate(`/lead-details/${lead.source || "generic"}/${lead._id}`)}
                                >
                                  <i className="mdi mdi-eye-outline text-primary" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    /* 👤 EMPLOYEE STREAMLINED VIEW (STAGE, ASSIGNED TO / UNASSIGNED, CREATED & UPDATED DATE) */
                    <div className="table-responsive">
                      <Table hover className="align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Lead Name</th>
                            <th>Stage</th>
                            <th>Assigned To</th>
                            <th>Approval Status</th>
                            <th>Created Date</th>
                            <th>Updated Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {leads.map((lead) => (
                            <tr key={lead._id}>
                              <td>
                                <div className="fw-semibold text-dark">{lead.name || "-"}</div>
                              </td>
                              <td>
                                <Badge color="info" pill className="px-2 py-1">
                                  {lead.stageName || "-"}
                                </Badge>
                              </td>
                              <td>
                                {lead.assignedToName && lead.assignedToName !== "-" ? (
                                  <span className="fw-semibold text-primary">{lead.assignedToName}</span>
                                ) : (
                                  <Badge color="light" className="text-secondary border px-2 py-1">
                                    Unassigned
                                  </Badge>
                                )}
                              </td>
                              <td>{renderApprovalStatusBadge(lead)}</td>
                              <td>
                                <span className="small text-muted">{formatDateTime(lead.createdAt || lead.createdTime)}</span>
                              </td>
                              <td>
                                <span className="small text-muted">{formatDateTime(lead.updatedAt)}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}

                  {/* PAGINATION */}
                  {pagination.totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
                      <span className="small text-muted">
                        Page {pagination.page} of {pagination.totalPages} ({pagination.total} leads)
                      </span>
                      <div className="d-flex gap-1">
                        <Button
                          size="sm"
                          color="light"
                          disabled={pagination.page <= 1}
                          onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
                        >
                          <i className="mdi mdi-chevron-left" />
                        </Button>
                        <Button
                          size="sm"
                          color="light"
                          disabled={pagination.page >= pagination.totalPages}
                          onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
                        >
                          <i className="mdi mdi-chevron-right" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* TAB 2: PENDING APPROVALS (SUPERADMIN / REPORTING MANAGER) */}
              {activeTab === "pendingApprovals" && (
                <div>
                  {pendingLoading ? (
                    <div className="text-center py-5">
                      <Spinner color="warning" />
                      <p className="mt-2 text-muted">Loading pending approvals...</p>
                    </div>
                  ) : pendingLeads.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <i className="mdi mdi-check-all fs-1 text-success d-block mb-2" />
                      <h5>All caught up!</h5>
                      <p className="small mb-0">No leads waiting for approval right now.</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table hover className="align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Lead Name</th>
                            <th>Contact</th>
                            <th>Submitted By</th>
                            <th>Project</th>
                            <th>Stage</th>
                            <th>Assigned To</th>
                            <th>Submitted Date</th>
                            <th className="text-center" style={{ width: "200px" }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pendingLeads.map((lead) => (
                            <tr key={lead._id}>
                              <td>
                                <div className="fw-semibold text-primary">{lead.name || "-"}</div>
                              </td>
                              <td>
                                <div className="small text-muted">{lead.phone || "-"}</div>
                                <div className="small text-muted">{lead.email || "-"}</div>
                              </td>
                              <td>
                                <span className="badge bg-light text-dark border">
                                  {lead.createdByName || "Employee"}
                                </span>
                              </td>
                              <td>
                                <span className="small fw-semibold">{lead.projectName || "-"}</span>
                              </td>
                              <td>
                                <Badge color="info" pill className="px-2 py-1">
                                  {lead.stageName || "-"}
                                </Badge>
                              </td>
                              <td>
                                {lead.assignedToName && lead.assignedToName !== "-" ? (
                                  <span className="small fw-semibold text-primary">{lead.assignedToName}</span>
                                ) : (
                                  <Badge color="light" className="text-secondary border">
                                    Unassigned
                                  </Badge>
                                )}
                              </td>
                              <td>
                                <span className="small text-muted">{formatDateTime(lead.createdAt || lead.createdTime)}</span>
                              </td>
                              <td>
                                <div className="d-flex align-items-center justify-content-center gap-2">
                                  <Button
                                    color="success"
                                    size="sm"
                                    className="d-flex align-items-center gap-1"
                                    onClick={() => handleApprove(lead._id)}
                                  >
                                    <i className="mdi mdi-check" /> Approve
                                  </Button>
                                  <Button
                                    color="danger"
                                    size="sm"
                                    className="d-flex align-items-center gap-1"
                                    onClick={() => handleOpenReject(lead)}
                                  >
                                    <i className="mdi mdi-close" /> Reject
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}

                  {/* PENDING APPROVALS PAGINATION */}
                  {pendingPagination.totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
                      <span className="small text-muted">
                        Page {pendingPagination.page} of {pendingPagination.totalPages} ({pendingPagination.total} pending leads)
                      </span>
                      <div className="d-flex gap-1">
                        <Button
                          size="sm"
                          color="light"
                          disabled={pendingPagination.page <= 1}
                          onClick={() => setPendingPagination((p) => ({ ...p, page: p.page - 1 }))}
                        >
                          <i className="mdi mdi-chevron-left" />
                        </Button>
                        <Button
                          size="sm"
                          color="light"
                          disabled={pendingPagination.page >= pendingPagination.totalPages}
                          onClick={() => setPendingPagination((p) => ({ ...p, page: p.page + 1 }))}
                        >
                          <i className="mdi mdi-chevron-right" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      </div>

      {/* 📝 CREATE LEAD MODAL */}
      <CreateLeadModal
        isOpen={createModal}
        toggle={() => setCreateModal(false)}
        onSuccess={() => setRefreshKey((prev) => prev + 1)}
      />

      {/* ❌ REJECTION REASON MODAL */}
      <Modal isOpen={rejectModalOpen} toggle={() => setRejectModalOpen(false)} centered>
        <ModalHeader toggle={() => setRejectModalOpen(false)} className="bg-light text-danger">
          <i className="mdi mdi-close-circle-outline me-2" />
          Reject Lead Submission
        </ModalHeader>
        <form onSubmit={handleSubmitReject}>
          <ModalBody>
            <p className="text-muted small mb-3">
              You are rejecting the lead submission for <strong>{selectedLeadToReject?.name || "Lead"}</strong>.
              Please provide the reason for rejection below so the employee understands why it was rejected.
            </p>
            <FormGroup>
              <Label for="rejection-reason" className="fw-semibold">
                Reason for Rejection <span className="text-danger">*</span>
              </Label>
              <Input
                type="textarea"
                id="rejection-reason"
                rows="3"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="e.g., Incomplete phone details / Duplicate client / Out of target area..."
                required
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setRejectModalOpen(false)} disabled={rejectSubmitting}>
              Cancel
            </Button>
            <Button color="danger" type="submit" disabled={rejectSubmitting}>
              {rejectSubmitting ? (
                <>
                  <Spinner size="sm" className="me-2" /> Rejecting...
                </>
              ) : (
                <>
                  <i className="mdi mdi-close me-1" /> Confirm Rejection
                </>
              )}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* ℹ️ VIEW REJECTION REASON MODAL */}
      <Modal
        isOpen={viewReasonModal.open}
        toggle={() => setViewReasonModal({ open: false, reason: "", leadName: "" })}
        centered
      >
        <ModalHeader
          toggle={() => setViewReasonModal({ open: false, reason: "", leadName: "" })}
          className="bg-light text-danger"
        >
          <i className="mdi mdi-alert-circle-outline me-2" />
          Rejection Reason
        </ModalHeader>
        <ModalBody>
          <h6 className="fw-semibold mb-2">{viewReasonModal.leadName}</h6>
          <div className="p-3 bg-light rounded border text-danger">
            {viewReasonModal.reason || "No specific reason provided."}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setViewReasonModal({ open: false, reason: "", leadName: "" })}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default MyAddedLeads;
